import { useState, useEffect } from "react";
import { C, S, fontCond, GENDER, CURRENT_SEASON, PAST_SEASONS, genderColor } from "../constants";
import { WinRateBar } from "../components/ui";
import { getPlayerStats } from "../lib/helpers";
import { dbLoadSeasonRanking, dbLoadResultsBySeason } from "../lib/supabase";

const RESULTS_PAGE = 30;

// Aba "Temporadas": consulta somente leitura das temporadas anteriores
// (classificação final + resultados), por divisão.
export default function SeasonsScreen({ gender, isMobile }) {
  const [season, setSeason]   = useState(PAST_SEASONS[0] ?? null);
  const [view, setView]       = useState("ranking"); // "ranking" | "resultados"
  const [ranking, setRanking] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(RESULTS_PAGE);

  const color = genderColor(gender);

  useEffect(() => {
    if (season == null) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setVisible(RESULTS_PAGE);
    Promise.all([
      dbLoadSeasonRanking(season, gender),
      dbLoadResultsBySeason(season, gender),
    ]).then(([rk, rs]) => {
      if (cancelled) return;
      setRanking(rk);
      setResults(rs);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [season, gender]);

  const pill = (active) => ({
    ...S.btn(color, !active),
    padding: "8px 16px", minHeight: 38, fontSize: 13,
  });

  const hasData = ranking.length > 0 || results.length > 0;
  const resultsDesc = [...results].reverse();

  return (
    <div>
      <div style={isMobile ? S.cardMobile : S.card}>
        <div style={S.title}>🗂️ Temporadas — {GENDER.label[gender]}</div>

        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          Arquivo das temporadas anteriores. A temporada em andamento ({CURRENT_SEASON}) está nas abas Ranking, Resultados e Stats.
        </div>

        {PAST_SEASONS.length === 0 ? (
          <div style={{ color: C.muted }}>Ainda não há temporadas arquivadas.</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {PAST_SEASONS.map((y) => (
                <button key={y} style={pill(season === y)} onClick={() => setSeason(y)}>{y}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={pill(view === "ranking")} onClick={() => setView("ranking")}>🏅 Classificação final</button>
              <button style={pill(view === "resultados")} onClick={() => setView("resultados")}>📋 Resultados</button>
            </div>
          </>
        )}
      </div>

      {PAST_SEASONS.length > 0 && (
        <div style={isMobile ? S.cardMobile : S.card}>
          {loading && <div style={{ color: C.muted }}>Carregando…</div>}

          {!loading && !hasData && (
            <div style={{ color: C.muted }}>
              Não há dados de {season} para a divisão {GENDER.label[gender]}.
            </div>
          )}

          {/* ── Classificação final ─────────────────────────────────────── */}
          {!loading && hasData && view === "ranking" && (
            <>
              <div style={{ ...S.title, fontSize: 16 }}>
                Classificação final {season} — {ranking.length} atletas
              </div>
              {ranking.length === 0 && (
                <div style={{ color: C.muted }}>Classificação final não disponível para esta temporada.</div>
              )}
              {ranking.length > 0 && (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>#</th>
                      <th style={S.th}>Jogador</th>
                      <th style={S.th}>V / D</th>
                      {!isMobile && <th style={S.th}>Taxa</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.map((p) => {
                      const st = getPlayerStats(p.name, results);
                      return (
                        <tr key={p.playerId}>
                          <td style={{ ...S.td, fontFamily: fontCond, fontWeight: 900, fontSize: 16, width: 48, color: p.position <= 3 ? C.warning : C.muted }}>
                            {p.position === 1 ? "🥇" : p.position === 2 ? "🥈" : p.position === 3 ? "🥉" : p.position}
                          </td>
                          <td style={{ ...S.td, fontWeight: 600 }}>{p.name}</td>
                          <td style={S.td}>
                            {st.total > 0
                              ? <span><span style={{ color: C.success, fontWeight: 700 }}>{st.wins}V</span><span style={{ color: C.muted }}> / </span><span style={{ color: "#e53e3e", fontWeight: 700 }}>{st.losses}D</span></span>
                              : <span style={{ color: C.muted }}>—</span>}
                          </td>
                          {!isMobile && (
                            <td style={{ ...S.td, minWidth: 120 }}>
                              {st.total > 0 ? <WinRateBar rate={st.winRate}/> : <span style={{ color: C.muted }}>—</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}

          {/* ── Resultados ──────────────────────────────────────────────── */}
          {!loading && hasData && view === "resultados" && (
            <>
              <div style={{ ...S.title, fontSize: 16, justifyContent: "space-between" }}>
                <span>Resultados {season}</span>
                <span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>
                  {results.length} resultado{results.length !== 1 ? "s" : ""}
                </span>
              </div>
              {results.length === 0 && <div style={{ color: C.muted }}>Nenhum resultado registrado.</div>}
              {resultsDesc.slice(0, visible).map((r, i) => {
                const chrWon = r.type === "wo-challenged" || (r.type !== "wo-challenger" && r.winner === "challenger");
                const chdWon = r.type === "wo-challenger" || (r.type !== "wo-challenged" && r.winner === "challenged");
                const isWo = r.type === "wo-challenged" || r.type === "wo-challenger";
                return (
                  <div key={r.id ?? i} style={{ padding: "10px 0", borderBottom: "1px solid " + C.border }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: C.muted }}>
                        {r.date ? new Date(r.date).toLocaleDateString("pt-BR") : "—"}
                      </span>
                      {isWo && <span style={S.badge(C.warning)}>W.O.</span>}
                    </div>
                    <div style={{ fontSize: 13 }}>
                      <span style={{ fontWeight: chrWon ? 700 : 400, color: chrWon ? C.success : C.muted }}>{r.challengerName}</span>
                      <span style={{ margin: "0 6px", color: C.muted }}>vs</span>
                      <span style={{ fontWeight: chdWon ? 700 : 400, color: chdWon ? C.info : C.muted }}>{r.challengedName}</span>
                      {r.score && <span style={{ fontFamily: fontCond, fontWeight: 700, marginLeft: 8, color: C.text }}>{r.score}</span>}
                    </div>
                  </div>
                );
              })}
              {visible < resultsDesc.length && (
                <button
                  style={{ ...S.btn(color, true), width: "100%", marginTop: 12 }}
                  onClick={() => setVisible((v) => v + RESULTS_PAGE)}
                >
                  Mostrar mais
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
